// Platform Analytics (Admin) — god-mode intelligence.
//
// Source-of-truth: /var/www/kitchenos/admin-standalone.html
//   page section:   line 1365  (#page-analytics)
//   KPI grid:       line 1372  (#a-kpis)
//   chart hosts:    line 1374-1389 (revenue / status donut / commission / supplier mix)
//
// Backend wiring
// --------------
// These admin endpoints do not exist yet:
//   GET /api/admin/analytics/summary?period=…   KPIs (turnover, commission, counts)
//   GET /api/admin/analytics/revenue?period=…   monthly revenue series
//   GET /api/admin/analytics/top-retailers?period=…
//   GET /api/admin/analytics/funnel?period=…    platform-wide quote funnel
//   GET /api/admin/analytics/supplier-mix?period=…
//   GET /api/admin/analytics/cohort-retention?period=…
//
// Stat endpoint that DOES exist: GET /api/admin/stats (server.js:864) —
// provides retailer/supplier counts. We hit it for the counts KPI and fan
// out optimistically for everything else, falling back to demo on 404/501.
//
// Charts are plain SVG (no dependency); per the rebuild contract.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../types";

import "./styles.css";

// ── Period domain ────────────────────────────────────────────────────────────

type PeriodKey = "7d" | "30d" | "90d" | "ytd" | "all";

interface PeriodSpec {
  key: PeriodKey;
  label: string;
  short: string;
  /** Number of monthly buckets the revenue line should span. */
  months: number;
}

const PERIODS: readonly PeriodSpec[] = [
  { key: "7d",  label: "7 days",   short: "7d",  months: 1 },
  { key: "30d", label: "30 days",  short: "30d", months: 1 },
  { key: "90d", label: "90 days",  short: "90d", months: 3 },
  { key: "ytd", label: "YTD",      short: "YTD", months: 12 },
  { key: "all", label: "All time", short: "All", months: 18 },
];

const DEFAULT_PERIOD: PeriodKey = "30d";

// ── Types ────────────────────────────────────────────────────────────────────

interface KpiSummary {
  totalTurnoverGbp: number;
  totalCommissionGbp: number;
  retailers: number;
  suppliers: number;
  customers: number;
  conversionRatePct: number;
  industryConversionRatePct: number;
}

interface RevenueSeries {
  months: string[];
  turnoverGbp: number[];
  commissionGbp: number[];
}

interface RetailerRow {
  id: string;
  name: string;
  turnoverGbp: number;
}

interface FunnelStage {
  stage: string;
  count: number;
  colour?: string;
}

interface SupplierMix {
  name: string;
  gmvGbp: number;
  colour?: string;
}

interface CohortCell {
  /** "2025-01" etc. */
  cohort: string;
  /** Signups in that month. */
  signups: number;
  /** Retention values indexed by months-since-signup. */
  retention: number[];
}

interface AnalyticsView {
  kpis: KpiSummary;
  revenue: RevenueSeries;
  topRetailers: RetailerRow[];
  funnel: FunnelStage[];
  supplierMix: SupplierMix[];
  cohort: CohortCell[] | null;
  isDemo: boolean;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const GBP_K = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  notation: "compact",
  maximumFractionDigits: 1,
});

const NUM = new Intl.NumberFormat("en-GB");

const PCT = new Intl.NumberFormat("en-GB", {
  style: "percent",
  maximumFractionDigits: 1,
});

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Demo fallback ────────────────────────────────────────────────────────────

function demoView(period: PeriodSpec): AnalyticsView {
  const months = monthLabels(period.months);
  // Trend seed scaled by period length.
  const seedTurnover = [128_000, 144_000, 132_000, 168_000, 192_000, 215_000, 198_000, 224_000, 246_000, 268_000, 285_000, 310_000];
  const turnoverGbp = pickSeries(seedTurnover, period.months);
  const commissionGbp = turnoverGbp.map((t) => Math.round(t * 0.033 * 0.92)); // ~3.3% capped

  const totalTurnover = turnoverGbp.reduce((a, b) => a + b, 0);
  const totalCommission = commissionGbp.reduce((a, b) => a + b, 0);

  const kpis: KpiSummary = {
    totalTurnoverGbp: totalTurnover,
    totalCommissionGbp: totalCommission,
    retailers: 42,
    suppliers: 6,
    customers: 1180,
    conversionRatePct: 0.196,
    industryConversionRatePct: 0.142,
  };

  const topRetailers: RetailerRow[] = [
    { id: "r_sjb",      name: "SJB Trade Kitchens",     turnoverGbp: 84_200 },
    { id: "r_manc",     name: "Manchester Cucina",       turnoverGbp: 71_800 },
    { id: "r_oldham",   name: "Oldham Kitchens",         turnoverGbp: 63_500 },
    { id: "r_leeds",    name: "Leeds Kitchen Studio",    turnoverGbp: 57_900 },
    { id: "r_sheff",    name: "Sheffield Cucina",        turnoverGbp: 49_200 },
    { id: "r_york",     name: "York Kitchen Workshop",   turnoverGbp: 41_700 },
    { id: "r_bolton",   name: "Bolton Kitchen Co",       turnoverGbp: 34_500 },
    { id: "r_warring",  name: "Warrington Kitchens",     turnoverGbp: 31_200 },
    { id: "r_stockport", name: "Stockport Kitchen Studio", turnoverGbp: 28_900 },
    { id: "r_chester",  name: "Chester Kitchen Co",      turnoverGbp: 26_400 },
  ];

  const funnel: FunnelStage[] = [
    { stage: "Lead",     count: 2840, colour: "#6CA0FF" },
    { stage: "Quote",    count: 1620, colour: "#7EA9FF" },
    { stage: "Approved", count:  610, colour: "#F59E0B" },
    { stage: "Won",      count:  556, colour: "#10B981" },
  ];

  const supplierMix: SupplierMix[] = [
    { name: "TKC",    gmvGbp: 412_000, colour: "#F59E0B" },
    { name: "PWS",    gmvGbp: 318_000, colour: "#6CA0FF" },
    { name: "Omega",  gmvGbp: 246_000, colour: "#10B981" },
    { name: "LDL",    gmvGbp: 182_000, colour: "#C5A8FF" },
    { name: "Blum",   gmvGbp:  98_000, colour: "#FF8EA0" },
    { name: "Häfele", gmvGbp:  62_000, colour: "#9BD1FF" },
  ];

  const cohort = demoCohort(months.slice(-6));

  return {
    kpis,
    revenue: { months, turnoverGbp, commissionGbp },
    topRetailers,
    funnel,
    supplierMix,
    cohort,
    isDemo: true,
  };
}

function monthLabels(n: number): string[] {
  const fmt = new Intl.DateTimeFormat("en-GB", { month: "short" });
  const out: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(fmt.format(d));
  }
  return out;
}

function pickSeries(seed: number[], n: number): number[] {
  if (n <= seed.length) return seed.slice(seed.length - n);
  const out = [...seed];
  while (out.length < n) {
    // Wrap around the seed so we never index past it.
    const idx = ((seed.length - out.length - 1) % seed.length + seed.length) % seed.length;
    const sample = seed[idx] ?? 0;
    out.unshift(Math.round(sample * 0.9));
  }
  return out;
}

function demoCohort(monthsLabels: string[]): CohortCell[] {
  // Synthetic but plausible retention curve. Each cohort decays a bit.
  return monthsLabels.map((m, i) => {
    const signups = 6 + Math.round(Math.random() * 8) + i;
    const decay = 0.88 - i * 0.015;
    const retention: number[] = [];
    for (let k = 0; k <= 5 - i; k++) {
      const r = k === 0 ? 1 : Math.max(0.4, decay - 0.07 * k);
      retention.push(r);
    }
    return { cohort: m, signups, retention };
  });
}

// ── Backend fetch ────────────────────────────────────────────────────────────

interface ServerStats {
  retailers: number;
  onboardings: number;
}

async function loadView(
  ctx: PageContext,
  period: PeriodSpec,
  signal: AbortSignal,
): Promise<AnalyticsView> {
  const q = `?period=${encodeURIComponent(period.key)}`;
  const [statsRes, summaryRes, revRes, topRes, funnelRes, mixRes, cohortRes] =
    await Promise.allSettled([
      ctx.api<ServerStats>("/admin/stats", { signal }),
      ctx.api<KpiSummary>(`/admin/analytics/summary${q}`, { signal }),
      ctx.api<RevenueSeries>(`/admin/analytics/revenue${q}`, { signal }),
      ctx.api<RetailerRow[]>(`/admin/analytics/top-retailers${q}`, { signal }),
      ctx.api<FunnelStage[]>(`/admin/analytics/funnel${q}`, { signal }),
      ctx.api<SupplierMix[]>(`/admin/analytics/supplier-mix${q}`, { signal }),
      ctx.api<CohortCell[]>(`/admin/analytics/cohort-retention${q}`, { signal }),
    ]);

  if (signal.aborted) {
    throw new DOMException("aborted", "AbortError");
  }

  const isMissing = (r: PromiseSettledResult<unknown>): boolean =>
    r.status === "rejected" &&
    r.reason instanceof ApiError &&
    (r.reason.status === 404 || r.reason.status === 501);

  // Surface the first non-404 error.
  for (const r of [summaryRes, revRes, topRes, funnelRes, mixRes, cohortRes]) {
    if (r.status === "rejected" && !isMissing(r)) {
      throw r.reason;
    }
  }
  // stats endpoint is real — non-404 errors there also bubble up.
  if (statsRes.status === "rejected" && !isMissing(statsRes)) {
    throw statsRes.reason;
  }

  const fallback = demoView(period);
  const view: AnalyticsView = {
    kpis: summaryRes.status === "fulfilled" ? summaryRes.value : fallback.kpis,
    revenue: revRes.status === "fulfilled" ? revRes.value : fallback.revenue,
    topRetailers: topRes.status === "fulfilled" ? topRes.value : fallback.topRetailers,
    funnel: funnelRes.status === "fulfilled" ? funnelRes.value : fallback.funnel,
    supplierMix: mixRes.status === "fulfilled" ? mixRes.value : fallback.supplierMix,
    cohort: cohortRes.status === "fulfilled" ? cohortRes.value : fallback.cohort,
    isDemo:
      summaryRes.status !== "fulfilled" ||
      revRes.status !== "fulfilled" ||
      topRes.status !== "fulfilled" ||
      funnelRes.status !== "fulfilled" ||
      mixRes.status !== "fulfilled",
  };

  // If we have a real stats response, overlay live retailer/supplier counts.
  if (statsRes.status === "fulfilled") {
    view.kpis = {
      ...view.kpis,
      retailers: statsRes.value.retailers ?? view.kpis.retailers,
    };
  }

  return view;
}

// ── SVG chart builders ───────────────────────────────────────────────────────

const CHART_PRIMARY = "#F59E0B";
const CHART_GRID = "rgba(148, 163, 184, 0.18)";
const CHART_TXT = "#9CA3AF";

/** Revenue trend — solid amber line + light commission line. */
function svgRevenueTrend(series: RevenueSeries): string {
  const W = 720;
  const H = 260;
  const padL = 60;
  const padR = 16;
  const padT = 16;
  const padB = 30;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const all = [...series.turnoverGbp, ...series.commissionGbp, 0];
  const max = Math.max(...all) * 1.1 || 1;

  const xFor = (i: number, n: number): number =>
    padL + plotW * (n <= 1 ? 0 : i / (n - 1));
  const yFor = (v: number): number => padT + plotH * (1 - v / max);

  const grid: string[] = [];
  for (let g = 0; g <= 4; g++) {
    const y = padT + plotH * (g / 4);
    const v = max - (max * g) / 4;
    grid.push(
      `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="${CHART_GRID}" stroke-width="1"/>`,
      `<text x="${padL - 8}" y="${y + 3}" fill="${CHART_TXT}" font-size="10" text-anchor="end">${escapeHtml(GBP_K.format(v))}</text>`,
    );
  }
  const xLabels = series.months
    .map((m, i) => {
      const x = xFor(i, series.months.length);
      return `<text x="${x}" y="${H - 10}" fill="${CHART_TXT}" font-size="10" text-anchor="middle">${escapeHtml(m)}</text>`;
    })
    .join("");

  const pathFor = (vals: number[]): string =>
    vals
      .map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i, vals.length).toFixed(1)} ${yFor(v).toFixed(1)}`)
      .join(" ");

  const tPath = pathFor(series.turnoverGbp);
  const cPath = pathFor(series.commissionGbp);

  const last = series.turnoverGbp.length;
  const area =
    tPath +
    ` L ${xFor(last - 1, last).toFixed(1)} ${(padT + plotH).toFixed(1)}` +
    ` L ${xFor(0, last).toFixed(1)} ${(padT + plotH).toFixed(1)} Z`;

  const dots = series.turnoverGbp
    .map((v, i) => {
      const x = xFor(i, last);
      const y = yFor(v);
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" fill="${CHART_PRIMARY}"/>`;
    })
    .join("");

  return `
    <svg class="ana-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Revenue trend">
      <defs>
        <linearGradient id="ana-rev-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"  stop-color="${CHART_PRIMARY}" stop-opacity="0.35"/>
          <stop offset="100%" stop-color="${CHART_PRIMARY}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${grid.join("")}
      ${xLabels}
      <path d="${area}" fill="url(#ana-rev-fill)"/>
      <path d="${cPath}" stroke="${CHART_TXT}" stroke-width="1.6" stroke-dasharray="4 4" fill="none" opacity="0.8"/>
      <path d="${tPath}" stroke="${CHART_PRIMARY}" stroke-width="2.2" fill="none"/>
      ${dots}
      <g class="ana-legend" font-size="10">
        <rect x="${padL}" y="${padT - 8}" width="10" height="3" fill="${CHART_PRIMARY}"/>
        <text x="${padL + 14}" y="${padT - 4}" fill="${CHART_TXT}">Turnover</text>
        <rect x="${padL + 80}" y="${padT - 8}" width="10" height="3" fill="${CHART_TXT}"/>
        <text x="${padL + 94}" y="${padT - 4}" fill="${CHART_TXT}">Commission</text>
      </g>
    </svg>
  `;
}

/** Horizontal bar list — top retailers by turnover. */
function renderTopRetailers(rows: RetailerRow[]): string {
  if (rows.length === 0) {
    return `<div class="adm-empty">No data yet.</div>`;
  }
  const max = Math.max(1, ...rows.map((r) => r.turnoverGbp));
  return `<ul class="ana-top-list">${rows
    .slice(0, 10)
    .map((r, i) => {
      const pct = (r.turnoverGbp / max) * 100;
      return `
        <li class="ana-top-row">
          <span class="ana-top-rank">${i + 1}</span>
          <span class="ana-top-name">${escapeHtml(r.name)}</span>
          <span class="ana-top-bar"><span class="ana-top-bar-fill" style="width:${pct.toFixed(1)}%"></span></span>
          <span class="ana-top-val">${escapeHtml(GBP.format(r.turnoverGbp))}</span>
        </li>
      `;
    })
    .join("")}</ul>`;
}

/** Funnel — 4-stage horizontal trapezoids (Lead → Quote → Approved → Won). */
function svgFunnel(stages: FunnelStage[]): string {
  const W = 360;
  const H = 260;
  const padX = 16;
  const padY = 12;
  const rowH = (H - padY * 2) / Math.max(stages.length, 1);
  const max = stages[0]?.count ?? 1;
  const fallback = ["#6CA0FF", "#7EA9FF", "#F59E0B", "#10B981"];

  const rows = stages
    .map((st, i) => {
      const colour = st.colour ?? fallback[i % fallback.length]!;
      const frac = max > 0 ? st.count / max : 0;
      const barW = (W - padX * 2) * frac;
      const cx = W / 2;
      const top = padY + i * rowH + 4;
      const bot = padY + (i + 1) * rowH - 6;
      const left = cx - barW / 2;
      const right = cx + barW / 2;
      const inset = Math.min(6, barW / 4);
      const points = [
        [left, top],
        [right, top],
        [right - inset, bot],
        [left + inset, bot],
      ]
        .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
        .join(" ");
      const labelY = top + (bot - top) / 2 + 4;
      return `
        <polygon points="${points}" fill="${colour}" opacity="0.92"/>
        <text x="${cx}" y="${labelY.toFixed(1)}" fill="#fff" font-size="12" font-weight="600" text-anchor="middle">${escapeHtml(st.stage)} · ${NUM.format(st.count)}</text>
      `;
    })
    .join("");

  return `
    <svg class="ana-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Quote funnel">
      ${rows}
    </svg>
  `;
}

/** Vertical bars — supplier mix by GMV. */
function svgSupplierMix(slices: SupplierMix[]): string {
  const W = 480;
  const H = 260;
  const padL = 28;
  const padR = 14;
  const padT = 24;
  const padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...slices.map((s) => s.gmvGbp)) * 1.1;
  const gap = 12;
  const barW = (plotW - gap * (slices.length - 1)) / Math.max(slices.length, 1);

  const bars = slices
    .map((s, i) => {
      const x = padL + i * (barW + gap);
      const h = plotH * (s.gmvGbp / max);
      const y = padT + plotH - h;
      const r = Math.min(4, barW / 4);
      const colour = s.colour ?? CHART_PRIMARY;
      const path = `M ${x} ${y + r} Q ${x} ${y} ${x + r} ${y} L ${x + barW - r} ${y} Q ${x + barW} ${y} ${x + barW} ${y + r} L ${x + barW} ${y + h} L ${x} ${y + h} Z`;
      return `
        <path d="${path}" fill="${colour}"/>
        <text x="${(x + barW / 2).toFixed(1)}" y="${(y - 6).toFixed(1)}" fill="#f0f2f6" font-size="10" font-weight="600" text-anchor="middle">${escapeHtml(GBP_K.format(s.gmvGbp))}</text>
        <text x="${(x + barW / 2).toFixed(1)}" y="${(H - 14).toFixed(1)}" fill="${CHART_TXT}" font-size="10" text-anchor="middle">${escapeHtml(s.name)}</text>
      `;
    })
    .join("");

  const baseline = `<line x1="${padL}" y1="${padT + plotH}" x2="${W - padR}" y2="${padT + plotH}" stroke="${CHART_GRID}" stroke-width="1"/>`;

  return `
    <svg class="ana-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Supplier mix by GMV">
      ${baseline}
      ${bars}
    </svg>
  `;
}

/** Industry vs platform conversion — paired vertical bars. */
function svgConversionCompare(platform: number, industry: number): string {
  const W = 280;
  const H = 200;
  const padX = 30;
  const padT = 30;
  const padB = 36;
  const plotH = H - padT - padB;
  const max = Math.max(platform, industry) * 1.15 || 1;
  const groupW = W - padX * 2;
  const barW = groupW * 0.32;
  const c1 = padX + groupW * 0.2 - barW / 2;
  const c2 = padX + groupW * 0.7 - barW / 2;

  const renderBar = (x: number, v: number, colour: string, label: string): string => {
    const h = plotH * (v / max);
    const y = padT + plotH - h;
    return `
      <rect x="${x}" y="${y}" width="${barW}" height="${h}" rx="3" fill="${colour}"/>
      <text x="${x + barW / 2}" y="${(y - 6).toFixed(1)}" fill="#f0f2f6" font-size="11" font-weight="700" text-anchor="middle">${escapeHtml(PCT.format(v))}</text>
      <text x="${x + barW / 2}" y="${H - 14}" fill="${CHART_TXT}" font-size="10" text-anchor="middle">${escapeHtml(label)}</text>
    `;
  };

  return `
    <svg class="ana-svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Conversion vs industry average">
      <line x1="${padX}" y1="${padT + plotH}" x2="${W - padX}" y2="${padT + plotH}" stroke="${CHART_GRID}"/>
      ${renderBar(c1, industry, CHART_TXT, "Industry")}
      ${renderBar(c2, platform, CHART_PRIMARY, "Platform")}
    </svg>
  `;
}

/** Cohort retention — heatmap-style matrix. */
function renderCohort(cells: CohortCell[] | null): string {
  if (!cells || cells.length === 0) {
    return `<div class="adm-empty">Cohort retention data unavailable.</div>`;
  }
  const maxBuckets = Math.max(...cells.map((c) => c.retention.length));
  const header = `<tr><th>Cohort</th><th class="ana-num">Signups</th>${Array.from({ length: maxBuckets }, (_, i) => `<th class="ana-num">+${i}m</th>`).join("")}</tr>`;
  const rows = cells
    .map((c) => {
      const ret = c.retention
        .map((v) => {
          const op = 0.2 + v * 0.7;
          return `<td class="ana-num"><span class="ana-cohort-cell" style="background:rgba(245,158,11,${op.toFixed(2)})">${escapeHtml(PCT.format(v))}</span></td>`;
        })
        .join("");
      const pad = Array.from({ length: maxBuckets - c.retention.length }, () => `<td class="ana-num">—</td>`).join("");
      return `<tr><td>${escapeHtml(c.cohort)}</td><td class="ana-num">${NUM.format(c.signups)}</td>${ret}${pad}</tr>`;
    })
    .join("");
  return `<table class="ana-cohort-table"><thead>${header}</thead><tbody>${rows}</tbody></table>`;
}

// ── Shell + rendering ────────────────────────────────────────────────────────

interface ShellRefs {
  rangeEl: HTMLElement;
  bannerEl: HTMLElement;
  kpisEl: HTMLElement;
  trendEl: HTMLElement;
  topEl: HTMLElement;
  funnelEl: HTMLElement;
  mixEl: HTMLElement;
  compareEl: HTMLElement;
  cohortEl: HTMLElement;
}

function renderShell(container: HTMLElement, period: PeriodSpec): ShellRefs {
  const tabs = PERIODS.map(
    (p) => `
      <button
        class="ana-range${p.key === period.key ? " is-active" : ""}"
        data-period="${p.key}"
        type="button"
        role="tab"
        aria-selected="${p.key === period.key}"
      >${escapeHtml(p.label)}</button>
    `,
  ).join("");

  container.innerHTML = `
    <section class="ana-page" aria-labelledby="ana-title">
      <header class="ana-head">
        <div>
          <h1 id="ana-title" class="ana-title">Platform Analytics</h1>
          <p class="ana-sub">Turnover, commission, conversion and retention across every retailer on TKOS.</p>
        </div>
        <div class="ana-range-wrap" role="tablist" data-role="range">${tabs}</div>
      </header>

      <div class="ana-banner" data-role="banner" hidden></div>

      <div class="ana-kpis" data-role="kpis"></div>

      <div class="ana-grid">
        <section class="adm-card ana-card ana-card--col8" aria-labelledby="ana-trend-h">
          <header class="ana-card-head">
            <h3 id="ana-trend-h">Revenue trend</h3>
            <p class="ana-card-sub">Turnover (solid) and commission earned (dashed)</p>
          </header>
          <div class="ana-card-body" data-role="trend"></div>
        </section>

        <section class="adm-card ana-card ana-card--col4" aria-labelledby="ana-comp-h">
          <header class="ana-card-head">
            <h3 id="ana-comp-h">Conversion vs industry</h3>
            <p class="ana-card-sub">Industry benchmark vs TKOS platform average</p>
          </header>
          <div class="ana-card-body" data-role="compare"></div>
        </section>

        <section class="adm-card ana-card ana-card--col6" aria-labelledby="ana-top-h">
          <header class="ana-card-head">
            <h3 id="ana-top-h">Top 10 retailers by turnover</h3>
            <p class="ana-card-sub">Ranked for the selected period</p>
          </header>
          <div class="ana-card-body" data-role="top"></div>
        </section>

        <section class="adm-card ana-card ana-card--col6" aria-labelledby="ana-funnel-h">
          <header class="ana-card-head">
            <h3 id="ana-funnel-h">Platform quote funnel</h3>
            <p class="ana-card-sub">Lead → Quote → Approved → Won</p>
          </header>
          <div class="ana-card-body" data-role="funnel"></div>
        </section>

        <section class="adm-card ana-card ana-card--col6" aria-labelledby="ana-mix-h">
          <header class="ana-card-head">
            <h3 id="ana-mix-h">Supplier mix by GMV</h3>
            <p class="ana-card-sub">Wholesale spend per supplier in the selected period</p>
          </header>
          <div class="ana-card-body" data-role="mix"></div>
        </section>

        <section class="adm-card ana-card ana-card--col6" aria-labelledby="ana-cohort-h">
          <header class="ana-card-head">
            <h3 id="ana-cohort-h">Cohort retention</h3>
            <p class="ana-card-sub">Retailer signups by month vs months retained</p>
          </header>
          <div class="ana-card-body" data-role="cohort"></div>
        </section>
      </div>
    </section>
  `;

  return {
    rangeEl: container.querySelector<HTMLElement>("[data-role='range']")!,
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    kpisEl: container.querySelector<HTMLElement>("[data-role='kpis']")!,
    trendEl: container.querySelector<HTMLElement>("[data-role='trend']")!,
    topEl: container.querySelector<HTMLElement>("[data-role='top']")!,
    funnelEl: container.querySelector<HTMLElement>("[data-role='funnel']")!,
    mixEl: container.querySelector<HTMLElement>("[data-role='mix']")!,
    compareEl: container.querySelector<HTMLElement>("[data-role='compare']")!,
    cohortEl: container.querySelector<HTMLElement>("[data-role='cohort']")!,
  };
}

interface KpiCard {
  label: string;
  value: string;
  hint?: string;
}

function renderKpis(host: HTMLElement, view: AnalyticsView, period: PeriodSpec): void {
  const cards: KpiCard[] = [
    { label: `Total turnover (${period.short})`, value: GBP.format(view.kpis.totalTurnoverGbp) },
    { label: `Commission (${period.short})`,     value: GBP.format(view.kpis.totalCommissionGbp), hint: "~3.3% rate, capped £500/txn" },
    { label: "Retailers",  value: NUM.format(view.kpis.retailers) },
    { label: "Suppliers",  value: NUM.format(view.kpis.suppliers) },
    { label: "Customers",  value: NUM.format(view.kpis.customers) },
    { label: "Conversion", value: PCT.format(view.kpis.conversionRatePct), hint: `industry ${PCT.format(view.kpis.industryConversionRatePct)}` },
  ];
  host.innerHTML = cards
    .map(
      (c) => `
        <article class="ana-kpi">
          <div class="ana-kpi-lbl">${escapeHtml(c.label)}</div>
          <div class="ana-kpi-val">${escapeHtml(c.value)}</div>
          ${c.hint ? `<div class="ana-kpi-hint">${escapeHtml(c.hint)}</div>` : ""}
        </article>
      `,
    )
    .join("");
}

function renderAll(refs: ShellRefs, view: AnalyticsView, period: PeriodSpec): void {
  renderKpis(refs.kpisEl, view, period);
  refs.trendEl.innerHTML = svgRevenueTrend(view.revenue);
  refs.topEl.innerHTML = renderTopRetailers(view.topRetailers);
  refs.funnelEl.innerHTML = svgFunnel(view.funnel);
  refs.mixEl.innerHTML = svgSupplierMix(view.supplierMix);
  refs.compareEl.innerHTML = svgConversionCompare(
    view.kpis.conversionRatePct,
    view.kpis.industryConversionRatePct,
  );
  refs.cohortEl.innerHTML = renderCohort(view.cohort);
}

// ── Page ─────────────────────────────────────────────────────────────────────

const analyticsPage: Page = {
  name: "analytics",
  label: "Analytics",
  icon: "📊",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let currentPeriod: PeriodSpec = PERIODS.find((p) => p.key === DEFAULT_PERIOD)!;
    const refs = renderShell(container, currentPeriod);

    let controller: AbortController | null = null;
    let disposed = false;

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        refs.bannerEl.hidden = true;
        refs.bannerEl.textContent = "";
        refs.bannerEl.classList.remove("ana-banner--error");
        return;
      }
      refs.bannerEl.hidden = false;
      refs.bannerEl.textContent = msg;
      refs.bannerEl.classList.toggle("ana-banner--error", kind === "error");
    };

    const setActiveTab = (key: PeriodKey): void => {
      refs.rangeEl.querySelectorAll<HTMLButtonElement>("[data-period]").forEach((btn) => {
        const active = btn.dataset.period === key;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-selected", String(active));
      });
    };

    const load = async (period: PeriodSpec): Promise<void> => {
      if (disposed) return;
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;
      container.querySelector(".ana-grid")?.classList.add("is-loading");
      try {
        const view = await loadView(ctx, period, signal);
        if (disposed || signal.aborted) return;
        renderAll(refs, view, period);
        setBanner(
          view.isDemo
            ? "Live analytics endpoints not yet available — showing realistic demo figures."
            : null,
          "info",
        );
      } catch (err) {
        if (controller?.signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load analytics (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load analytics";
        const fallback = demoView(period);
        renderAll(refs, fallback, period);
        setBanner(`${msg} — showing demo figures.`, "error");
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) {
          container.querySelector(".ana-grid")?.classList.remove("is-loading");
        }
      }
    };

    const onRangeClick = (ev: Event): void => {
      const target = ev.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>("[data-period]");
      if (!btn) return;
      const key = btn.dataset.period as PeriodKey | undefined;
      if (!key) return;
      const next = PERIODS.find((p) => p.key === key);
      if (!next || next.key === currentPeriod.key) return;
      currentPeriod = next;
      setActiveTab(key);
      void load(next);
    };

    refs.rangeEl.addEventListener("click", onRangeClick);

    void load(currentPeriod);

    return (): void => {
      disposed = true;
      refs.rangeEl.removeEventListener("click", onRangeClick);
      controller?.abort();
      controller = null;
    };
  },
};

registerPage(analyticsPage);
